import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { resetLionaChat } from '@/features/cs-chat/open-liona-chat'

import type { Session, User } from './schema'

interface AuthState {
  session: Session | null
  setSession: (session: Session) => void
  setUser: (user: User) => void
  clearSession: () => void
}

const STORAGE_KEY = 'robojastip:auth'

/**
 * Global session store. Persisted to localStorage so a refresh keeps the user
 * logged in; read synchronously by route guards via `useAuthStore.getState()`.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      setSession: (session) => {
        const tokenChanged = get().session?.accessToken !== session.accessToken
        set({ session })
        if (tokenChanged) resetLionaChat()
      },
      setUser: (user) =>
        set((state) => (state.session ? { session: { ...state.session, user } } : state)),
      clearSession: () => {
        set({ session: null })
        resetLionaChat()
      },
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      partialize: (state) => ({ session: state.session }),
    },
  ),
)

/** Non-reactive read for route guards and the API token provider. */
export function getAccessToken() {
  const { session } = useAuthStore.getState()
  if (!session) return null
  // A token the server will reject anyway is the same as no token.
  if (Date.parse(session.expiresAt) <= Date.now()) return null
  return session.accessToken
}

export function isAuthenticated() {
  return getAccessToken() !== null
}

export const useUser = () => useAuthStore((s) => s.session?.user ?? null)
